import { cn } from '@/utils/cn'
import { initials } from '@/utils/format'

// Token-based gradients (no raw colours); the same title always gets the same cover.
const gradients = [
  'from-brand-500 to-accent-600',
  'from-success-500 to-info-600',
  'from-warning-500 to-danger-500',
  'from-info-500 to-brand-600',
  'from-accent-500 to-danger-500',
  'from-brand-600 to-brand-950',
  'from-success-600 to-brand-600',
]

function gradientFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0
  return gradients[Math.abs(hash) % gradients.length] ?? 'from-brand-500 to-accent-600'
}

const sizes = {
  xs: 'w-9 rounded-xs text-2xs',
  sm: 'w-12 rounded-xs text-xs',
  md: 'w-full rounded-control text-2xl',
  lg: 'w-40 rounded-card text-4xl sm:w-48',
} as const

interface BookCoverProps {
  title: string
  authors?: string
  src?: string | null
  size?: keyof typeof sizes
  className?: string
}

/**
 * The uploaded cover, or a generated one: brand-token gradient, initials, title and author.
 * Decorative — the title is always rendered as text next to it, so it is hidden from AT.
 */
export function BookCover({ title, authors, src, size = 'md', className }: BookCoverProps) {
  const detailed = size === 'md' || size === 'lg'
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative aspect-[2/3] shrink-0 overflow-hidden shadow-card ring-1 ring-line',
        sizes[size],
        className,
      )}
    >
      {src ? (
        <img src={src} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        <div
          className={cn(
            'flex size-full flex-col bg-gradient-to-br text-white',
            gradientFor(title),
            detailed ? 'p-3' : 'items-center justify-center',
          )}
        >
          <span className="absolute inset-y-0 left-0 w-1.5 bg-white/15" />
          <span className={cn('font-semibold tracking-tight', detailed && 'mt-auto')}>{initials(title)}</span>
          {detailed ? (
            <>
              <span className="mt-2 line-clamp-3 text-xs leading-snug font-medium text-white/90">
                {title}
              </span>
              {authors ? <span className="mt-1 line-clamp-1 text-2xs text-white/70">{authors}</span> : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
